import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Activity, Save, Settings2, LineChart } from "lucide-react";
import { PlayerAvatar } from "@/components/PlayerAvatar";
import { toast } from "sonner";
import {
  gradeResult, aggregateAttempts, EVALUATION_LABELS, UNIT_SHORT, AGG_LABEL, currentSeason,
  CATEGORY_LABELS, recordGrade, testsForRecord,
} from "@/lib/physical";
import TestDefsManager from "@/components/physical/TestDefsManager";
import EvolutionDialog from "@/components/physical/EvolutionDialog";

const stripDiacritics = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function normaliseCategory(raw) {
  if (!raw) return "";
  const cleaned = stripDiacritics(raw.toLowerCase()).trim();
  const bases = ["prebenjamin", "benjamin", "alevin", "infantil", "cadete", "juvenil", "aficionado"];
  for (const b of bases) if (cleaned.startsWith(b)) return b;
  if (cleaned.startsWith("aficion")) return "aficionado";
  if (cleaned.startsWith("alev")) return "alevin";
  if (cleaned.startsWith("preben")) return "prebenjamin";
  if (cleaned.startsWith("ben")) return "benjamin";
  if (cleaned.startsWith("inf")) return "infantil";
  if (cleaned.startsWith("cad")) return "cadete";
  if (cleaned.startsWith("juv")) return "juvenil";
  return cleaned;
}

const EVALUATIONS = [1, 2, 3];

const defToCatalogEntry = (d) => ({
  id: d.id, label: d.name, unit: d.unit, direction: d.direction,
  attempts: d.attempts || 1, aggregation: d.aggregation || "best", description: d.description || "",
});

export default function PhysicalTests() {
  const { teamId: paramTeamId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [tests, setTests] = useState([]);
  const [defs, setDefs] = useState([]);
  const [baremos, setBaremos] = useState({});
  const [selectedTeamId, setSelectedTeamId] = useState(paramTeamId || "");
  const [season, setSeason] = useState(currentSeason());

  const [editing, setEditing] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [evolutionPlayer, setEvolutionPlayer] = useState(null);

  const loadCatalog = async () => {
    const [d, br] = await Promise.all([
      api.get("/physical-tests/defs"),
      api.get("/baremos"),
    ]);
    setDefs(d.data || []);
    setBaremos(br.data || {});
  };

  useEffect(() => {
    (async () => {
      const t = await api.get("/teams");
      setTeams(t.data || []);
      await loadCatalog();
      if (!paramTeamId && t.data?.length > 0) setSelectedTeamId(t.data[0].id);
    })().catch((err) => toast.error(formatApiError(err)));
  }, [paramTeamId]);

  useEffect(() => {
    if (!selectedTeamId) return;
    (async () => {
      const [pl, tr] = await Promise.all([
        api.get(`/players?team_id=${selectedTeamId}`),
        api.get(`/physical-tests?team_id=${selectedTeamId}&season=${encodeURIComponent(season)}`),
      ]);
      setPlayers((pl.data || []).sort((a, b) => (a.dorsal || 99) - (b.dorsal || 99)));
      setTests(tr.data || []);
    })().catch((err) => toast.error(formatApiError(err)));
  }, [selectedTeamId, season]);

  const team = teams.find((t) => t.id === selectedTeamId);
  const category = normaliseCategory(team?.category) || "alevin";

  const categoryTests = useMemo(
    () => (defs || [])
      .filter((d) => d.active !== false && (d.categories || []).includes(category))
      .map(defToCatalogEntry),
    [defs, category],
  );
  const categoryBaremos = baremos[category] || {};

  const hasHistoric = useMemo(
    () => tests.some((t) => Object.keys(t.results || {}).length > 0),
    [tests],
  );

  const testIndex = useMemo(() => {
    const m = new Map();
    tests.forEach((t) => m.set(`${t.player_id}::${t.evaluation}`, t));
    return m;
  }, [tests]);

  const openEditor = (player, evaluation) => {
    const existing = testIndex.get(`${player.id}::${evaluation}`);
    // Seed attempts: existing.attempts when present, else wrap legacy single value.
    const attempts = {};
    categoryTests.forEach((tt) => {
      const n = Math.max(1, tt.attempts || 1);
      const ex = existing?.attempts?.[tt.id];
      const legacy = existing?.results?.[tt.id];
      let arr;
      if (ex && ex.length) arr = ex.map((v) => (v == null ? "" : v));
      else if (legacy != null) arr = [legacy];
      else arr = [];
      while (arr.length < n) arr.push("");
      attempts[tt.id] = arr.slice(0, Math.max(n, arr.length));
    });
    setEditing({
      player, evaluation,
      date: existing?.date || new Date().toISOString().slice(0, 10),
      attempts,
      notes: existing?.notes || "",
      id: existing?.id,
    });
  };

  const save = async () => {
    if (!editing) return;
    try {
      const attemptsPayload = {};
      const resultsPayload = {};
      categoryTests.forEach((tt) => {
        const arr = editing.attempts[tt.id] || [];
        const nums = arr.filter((v) => v !== "" && v != null).map(Number);
        if (nums.length) attemptsPayload[tt.id] = nums;
        else resultsPayload[tt.id] = null; // clearing an active test wipes its stored value
      });
      const payload = {
        player_id: editing.player.id, team_id: selectedTeamId, season,
        evaluation: editing.evaluation, date: editing.date, category,
        attempts: attemptsPayload, results: resultsPayload, notes: editing.notes || "",
      };
      const r = await api.post("/physical-tests", payload);
      setTests((xs) => [r.data, ...xs.filter(
        (t) => !(t.player_id === editing.player.id && t.evaluation === editing.evaluation),
      )]);
      toast.success("Resultados guardados");
      setEditing(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: "Nunito" }}>
            Tests físicos
          </h1>
          <p className="text-sm text-slate-500">3 evaluaciones por temporada · Sep · Ene · May</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Equipo</Label>
            <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
              <SelectTrigger className="min-w-[220px]" data-testid="physical-team-select">
                <SelectValue placeholder="Selecciona equipo" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} <span className="text-xs text-slate-400">{t.category}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Temporada</Label>
            <Input value={season} onChange={(e) => setSeason(e.target.value)} className="w-28" data-testid="physical-season" />
          </div>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setManageOpen(true)} data-testid="open-tests-manager">
              <Settings2 className="h-4 w-4 mr-1.5" /> Gestionar pruebas
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4 text-purple-600" />
            Resultados · {CATEGORY_LABELS[category] || category} · {team?.name || ""}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {!team ? (
            <div className="py-6 text-sm text-slate-400 italic">Selecciona un equipo.</div>
          ) : players.length === 0 ? (
            <div className="py-6 text-sm text-slate-400 italic">El equipo no tiene jugadores.</div>
          ) : categoryTests.length === 0 && !hasHistoric ? (
            <div className="py-6 text-sm text-slate-400 italic">
              No hay pruebas activas para la categoría <b>{CATEGORY_LABELS[category] || category}</b>.
              {isAdmin && " Usa “Gestionar pruebas” para crear o asignar pruebas a esta categoría."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-2 py-2 text-left font-bold">Jugador</th>
                  {EVALUATIONS.map((ev) => (
                    <th key={ev} className="px-2 py-2 text-center font-bold">{EVALUATION_LABELS[ev]}</th>
                  ))}
                  <th className="px-2 py-2 text-center font-bold">Evolución</th>
                </tr>
              </thead>
              <tbody>
                {players.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-purple-50/30">
                    <td className="px-2 py-3 text-slate-800 font-semibold">
                      <div className="flex items-center gap-2">
                        <PlayerAvatar player={p} className="w-7 h-7" />
                        <span>{p.name}</span>
                      </div>
                    </td>
                    {EVALUATIONS.map((ev) => {
                      const t = testIndex.get(`${p.id}::${ev}`);
                      return (
                        <td key={ev} className="px-2 py-3 align-top">
                          <button type="button" onClick={() => openEditor(p, ev)}
                            className="w-full text-left rounded-md border border-slate-200 px-2 py-1.5 hover:border-purple-300 hover:bg-purple-50/40 transition"
                            data-testid={`physical-cell-${p.id}-${ev}`}>
                            {!t ? (
                              <span className="text-xs text-slate-400 italic">Sin registrar</span>
                            ) : (
                              <div className="space-y-0.5">
                                {testsForRecord(t, categoryTests).map((tt) => {
                                  const v = t.results?.[tt.id];
                                  if (tt.inactive && v == null) return null;
                                  const grade = recordGrade(t, tt.id, categoryBaremos[tt.id], tt.direction);
                                  return (
                                    <div key={tt.id} className="flex items-center justify-between gap-2">
                                      <span className="text-[11px] text-slate-500">
                                        {tt.label}{tt.inactive ? <span className="text-slate-400"> · inactiva</span> : null}
                                      </span>
                                      <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${grade.bg} ${grade.color}`}>
                                        {v != null ? v : "—"} {v != null ? (UNIT_SHORT[tt.unit] || "") : ""}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </button>
                        </td>
                      );
                    })}
                    <td className="px-2 py-3 text-center">
                      <Button variant="ghost" size="sm" onClick={() => setEvolutionPlayer(p)}
                        className="text-purple-600" data-testid={`physical-evolution-${p.id}`}>
                        <LineChart className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.player.name} · {EVALUATION_LABELS[editing.evaluation]}</DialogTitle>
              <DialogDescription className="text-xs">
                Categoría {CATEGORY_LABELS[category] || category} · temporada {season}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Fecha</Label>
                <Input type="date" value={editing.date}
                  onChange={(e) => setEditing({ ...editing, date: e.target.value })} data-testid="physical-date" />
              </div>
              {categoryTests.map((tt) => {
                const arr = editing.attempts[tt.id] || [""];
                const value = aggregateAttempts(arr, tt.aggregation, tt.direction);
                const grade = value != null ? gradeResult(value, categoryBaremos[tt.id], tt.direction) : null;
                const setAttempt = (i, val) => {
                  const next = [...arr];
                  next[i] = val === "" ? "" : Number(val);
                  setEditing({ ...editing, attempts: { ...editing.attempts, [tt.id]: next } });
                };
                return (
                  <div key={tt.id} className="border border-slate-200 rounded-lg p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <Label className="text-sm font-semibold">{tt.label}</Label>
                        <span className="text-[11px] text-slate-400 ml-1">
                          ({UNIT_SHORT[tt.unit]} · {AGG_LABEL[tt.aggregation]})
                        </span>
                      </div>
                      {grade && (
                        <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${grade.bg} ${grade.color}`}>
                          {value} {UNIT_SHORT[tt.unit]} · {grade.label}
                        </span>
                      )}
                    </div>
                    {tt.description && <p className="text-[11px] text-slate-400 mb-1.5">{tt.description}</p>}
                    <div className="flex flex-wrap gap-2">
                      {arr.map((a, i) => (
                        <div key={i} className="w-24">
                          <Label className="text-[10px] text-slate-500">Intento {i + 1}</Label>
                          <Input type="number" step="0.01" value={a}
                            onChange={(e) => setAttempt(i, e.target.value)}
                            placeholder={UNIT_SHORT[tt.unit]} data-testid={`attempt-${tt.id}-${i}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Notas</Label>
                <Input value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  placeholder="Notas internas..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={save} data-testid="save-physical">
                <Save className="h-4 w-4 mr-1.5" /> Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {manageOpen && (
        <TestDefsManager open onClose={() => setManageOpen(false)} onChanged={loadCatalog} />
      )}

      {evolutionPlayer && (
        <EvolutionDialog player={evolutionPlayer} categoryTests={categoryTests}
          baremos={{ ...categoryBaremos }} onClose={() => setEvolutionPlayer(null)} />
      )}
    </div>
  );
}
